import { Injectable, OnDestroy } from "@angular/core";
import { BehaviorSubject } from "rxjs/BehaviorSubject";
import { Subject } from "rxjs/Subject";
import { Observable } from "rxjs/Observable";
import { takeUntil } from "rxjs/operators";
import { CircularInjectionHelper } from "./circularInjectionHelper";
import { CoreLogService } from "./log.service";
import { IObjectObserver } from "./IObjectObserver";
import { ObjectObserverFactory } from "./objectObserverFactory";
import { SnackBarService } from "./snackBar.service";
import { environment } from "../../../environments/environment";
import Utils from "../shared/utils";
import { ContentType, SnackBarMessageType, YourAPPProductType } from "../shared/contentTypes";
import {
  AccountPaymentActivationStatus,
  AccountPaymentCreditValues,
  AccountPaymentLimitResult,
  AccountPaymentRowState,
  AccountPaymentRowStateContext,
  AccountingCustomerDataModel,
  evaluateAccountPaymentLimit,
  resolveAccountPaymentRowState,
} from "../shared/models/accountPayment.model";
import { WebshopSettingsDataModel2, WebshopSettingsDMKey } from "../shared/models";

@Injectable({ providedIn: "root" })
export class AccountPaymentService implements OnDestroy {

  private _destroy$ = new Subject<void>();
  private _statusSubject = new BehaviorSubject<AccountPaymentActivationStatus>(AccountPaymentActivationStatus.None);
  private _limitResultSubject = new BehaviorSubject<AccountPaymentLimitResult>({ isBlocked: false, isLimitReached: false, isOverLimit: false, exceedAmount: 0 });
  private _creditValuesSubject = new BehaviorSubject<AccountPaymentCreditValues | null>(null);
  private _accountingCustomerSubject = new BehaviorSubject<AccountingCustomerDataModel | null>(null);
  private _activationRequestInProgressSubject = new BehaviorSubject<boolean>(false);

  private _statusLoadedSubject = new BehaviorSubject<boolean>(false);
  private _statusGeneration: number = 0;
  private _isStatusLoading: boolean = false;
  private _isCreditLoading: boolean = false;

  private _webshopSettingsObsServ: IObjectObserver<WebshopSettingsDataModel2>;

  constructor(
    private _circularInjectionHelper: CircularInjectionHelper,
    private _coreLogService: CoreLogService,
    private _snackBarService: SnackBarService
  ) {
    const me = this;
    if (environment.producttype == YourAPPProductType.sot) {
      return;
    }
    me._webshopSettingsObsServ = ObjectObserverFactory
      .GetObjectObserverByInjectionHelper<WebshopSettingsDataModel2>(ContentType.WebshopSettings, me._circularInjectionHelper);
    me.refreshAccountingCustomerFromSettings();
    me._webshopSettingsObsServ.ObserveChanges
      .pipe(takeUntil(me._destroy$))
      .subscribe(() => me.refreshAccountingCustomerFromSettings());
  }

  public get activationRequestInProgress$(): Observable<boolean> {
    const me = this;
    return me._activationRequestInProgressSubject.asObservable();
  }

  public get isApproved(): boolean {
    const me = this;
    return me._statusSubject.getValue() === AccountPaymentActivationStatus.Approved;
  }

  /** True once the activation status was successfully fetched for the current session. */
  public get isStatusLoaded(): boolean {
    const me = this;
    return me._statusLoadedSubject.getValue();
  }

  public get statusLoaded$(): Observable<boolean> {
    const me = this;
    return me._statusLoadedSubject.asObservable();
  }

  public get isActivationRequestInProgress(): boolean {
    const me = this;
    return me._activationRequestInProgressSubject.getValue();
  }

  public get isPending(): boolean {
    const me = this;
    return me._statusSubject.getValue() === AccountPaymentActivationStatus.Pending;
  }

  public get status$(): Observable<AccountPaymentActivationStatus> {
    const me = this;
    return me._statusSubject.asObservable();
  }

  public get limitResult$(): Observable<AccountPaymentLimitResult> {
    const me = this;
    return me._limitResultSubject.asObservable();
  }

  public get creditValues$(): Observable<AccountPaymentCreditValues | null> {
    const me = this;
    return me._creditValuesSubject.asObservable();
  }

  public get accountingCustomer$(): Observable<AccountingCustomerDataModel | null> {
    const me = this;
    return me._accountingCustomerSubject.asObservable();
  }

  /** Loads the activation status; skips if a load is running or reset() ran meanwhile. */
  public async loadStatus(): Promise<void> {
    const me = this;
    if (me._isStatusLoading) { return; }
    me._isStatusLoading = true;
    const generation = me._statusGeneration;
    try {
      const response = await me._circularInjectionHelper.yourAppServer.User.GetAccountPaymentActivationStatus();
      if (generation !== me._statusGeneration) { return; }
      const status = me.tryParseStatus(response.status);
      if (status === null) {
        me._statusSubject.next(AccountPaymentActivationStatus.None);
        me._statusLoadedSubject.next(false);
        me._coreLogService.error(
          "AccountPaymentService.loadStatus",
          "GetAccountPaymentActivationStatus",
          "unknown status: " + String(response.status).substring(0, 100)
        );
        return;
      }
      me._statusSubject.next(status);
      me._statusLoadedSubject.next(true);
    } catch (error) {
      if (generation !== me._statusGeneration) { return; }
      me._statusSubject.next(AccountPaymentActivationStatus.None);
      me._statusLoadedSubject.next(false);
      me._coreLogService.error(
        "AccountPaymentService.loadStatus",
        "GetAccountPaymentActivationStatus",
        me.extractErrorDetail(error)
      );
    } finally {
      if (generation === me._statusGeneration) {
        me._isStatusLoading = false;
      }
    }
  }

  /** Submits the activation request and returns whether it ended Pending or Approved. */
  private async requestActivation(): Promise<boolean> {
    const me = this;
    const generation = me._statusGeneration;
    try {
      const response = await me._circularInjectionHelper.yourAppServer.User.RequestAccountPaymentActivation();
      if (generation !== me._statusGeneration) { return false; }
      const knownStatus = me.tryParseStatus(response.status);
      if (knownStatus === null) {
        me._statusSubject.next(AccountPaymentActivationStatus.None);
        me._coreLogService.error(
          "AccountPaymentService.requestActivation",
          "RequestAccountPaymentActivation",
          "unknown status: " + String(response.status).substring(0, 100)
        );
        return false;
      }
      me._statusSubject.next(knownStatus);
      me._statusLoadedSubject.next(true);
      return (knownStatus === AccountPaymentActivationStatus.Pending || knownStatus === AccountPaymentActivationStatus.Approved);
    } catch (error) {
      if (generation !== me._statusGeneration) { return false; }
      me._coreLogService.error(
        "AccountPaymentService.requestActivation",
        "RequestAccountPaymentActivation",
        me.extractErrorDetail(error)
      );
      return false;
    }
  }

  /** Sends the activation request once and shows the result snackbar. */
  public async submitActivationRequest(): Promise<void> {
    const me = this;
    if (me._activationRequestInProgressSubject.getValue()) { return; }
    const generation = me._statusGeneration;
    me._activationRequestInProgressSubject.next(true);
    try {
      const success = await me.requestActivation();
      if (generation !== me._statusGeneration) { return; }
      if (success) {
        me._snackBarService.ShowMessage("accountPaymentSuccess", SnackBarMessageType.Successful);
      } else {
        me._snackBarService.ShowMessage("accountPaymentError", SnackBarMessageType.Error);
      }
    } catch (error) {
      if (generation !== me._statusGeneration) { return; }
      me._snackBarService.ShowMessage("accountPaymentError", SnackBarMessageType.Error);
    } finally {
      if (generation === me._statusGeneration) {
        me._activationRequestInProgressSubject.next(false);
      }
    }
  }

  /** Resolves the account-payment row state from the service-owned status/loaded/in-flight plus the caller's context. */
  public resolveRowState(context: AccountPaymentRowStateContext): AccountPaymentRowState {
    const me = this;
    return resolveAccountPaymentRowState({
      ...context,
      isStatusLoaded: me.isStatusLoaded,
      status: me._statusSubject.getValue(),
      isRequestInFlight: me.isActivationRequestInProgress,
    });
  }

  public checkLimit(basketTotal: number): void {
    const me = this;
    const values = me._creditValuesSubject.getValue();
    me._limitResultSubject.next(evaluateAccountPaymentLimit(values, basketTotal));
  }

  public get canRequestActivation(): boolean {
    const me = this;
    const auth = me._circularInjectionHelper.yourAppServer.Auth;
    return auth.IsActive && !auth.IsNobody && !me.isPending && !me.isApproved;
  }

  /** Loads the current credit amounts (cents to euros) from the server; keeps the last value on error. */
  public async loadCreditInfo(): Promise<void> {
    const me = this;
    if (me._isCreditLoading) { return; }
    me._isCreditLoading = true;
    try {
      const response = await me._circularInjectionHelper.yourAppServer.User.GetAccountPaymentCredit();
      me._creditValuesSubject.next({
        available: Utils.centsToCurrency((response.available_cents != null) ? response.available_cents : 0),
        usedBalance: Utils.centsToCurrency((response.used_cents != null) ? response.used_cents : 0),
        limit: Utils.centsToCurrency((response.credit_limit_cents != null) ? response.credit_limit_cents : 0),
      });
    } catch (error) {
      me._coreLogService.error("AccountPaymentService.loadCreditInfo", "GetAccountPaymentCredit", me.extractErrorDetail(error));
    } finally {
      me._isCreditLoading = false;
    }
  }

  /** Clears per-user state on logout; keeps tenant accounting-customer data. */
  public reset(): void {
    const me = this;
    me._statusSubject.next(AccountPaymentActivationStatus.None);
    me._limitResultSubject.next({ isBlocked: false, isLimitReached: false, isOverLimit: false, exceedAmount: 0 });
    me._creditValuesSubject.next(null);
    me._activationRequestInProgressSubject.next(false);
    me._statusLoadedSubject.next(false);
    me._statusGeneration++;
    me._isStatusLoading = false;
    me._isCreditLoading = false;
  }

  /** True when account payment is enabled for at least one ordertype. */
  public isEnabledForAnyOrdertype(settings: WebshopSettingsDataModel2): boolean {
    if (!settings || !Array.isArray(settings.ordertypes)) { return false; }
    return settings.ordertypes.some(ordertype => ordertype.can_accounting && ordertype.use_accounting);
  }

  /** True when account payment is enabled specifically for the given ordertype id. */
  public isEnabledForOrdertype(settings: WebshopSettingsDataModel2, ordertypeId: number): boolean {
    if (!settings || !Array.isArray(settings.ordertypes)) { return false; }
    const match = settings.ordertypes.find(ordertype => ordertype.id === ordertypeId);
    if (!match) { return false; }
    return !!(match.can_accounting && match.use_accounting);
  }

  /** Publishes the tenant's accounting-customer config from settings (defaults when absent). */
  private refreshAccountingCustomerFromSettings(): void {
    const me = this;
    me._webshopSettingsObsServ.GetObject(WebshopSettingsDMKey)
      .then((settings) => {
        if (settings.accounting_customer == null) {
          me._coreLogService.info(
            "AccountPaymentService",
            "refreshAccountingCustomerFromSettings",
            "accounting_customer absent — using empty fallback"
          );
          me._accountingCustomerSubject.next(new AccountingCustomerDataModel());
          return;
        }
        const accountingCustomer = settings.accounting_customer;
        me._accountingCustomerSubject.next(accountingCustomer);
        me._seedCreditValuesFromDefault(accountingCustomer);
      })
      .catch((error) => {
        me._coreLogService.error(
          "AccountPaymentService",
          "refreshAccountingCustomerFromSettings",
          me.extractErrorDetail(error)
        );
      });
  }

  /** Seeds a placeholder credit value from the tenant default until loadCreditInfo() resolves; never overwrites a live value. */
  private _seedCreditValuesFromDefault(accountingCustomer: AccountingCustomerDataModel): void {
    const me = this;
    if (accountingCustomer.defaultCreditLimit > 0 && me._creditValuesSubject.getValue() == null) {
      const defaultEuros = accountingCustomer.defaultCreditLimit;
      me._creditValuesSubject.next({ available: defaultEuros, usedBalance: 0, limit: defaultEuros });
    }
  }

  public ngOnDestroy(): void {
    const me = this;
    me._destroy$.next();
    me._destroy$.complete();
  }

  /** Maps the raw server status (lowercased, no trim) to the enum; null for unknown/empty values, so "not loaded" stays distinguishable from None. */
  private tryParseStatus(raw: string): AccountPaymentActivationStatus | null {
    const normalized = (typeof raw === "string") ? raw.toLowerCase() : "";
    switch (normalized) {
      case "none": return AccountPaymentActivationStatus.None;
      case "pending": return AccountPaymentActivationStatus.Pending;
      case "rejected": return AccountPaymentActivationStatus.Rejected;
      case "approved": return AccountPaymentActivationStatus.Approved;
      default: return null;
    }
  }

  /** Extracts a loggable, bounded detail from a caught error. */
  private extractErrorDetail(error: any): string {
    return error ? (error.codeText || error.code || String(error.message || "") || (typeof error === "string" ? error : null) || "unknown") : "unknown";
  }
}
